import { FC } from "react";

import { LinkWithIcon } from "@/atoms/link-with-icon/LinkWithIcon";
import { cn } from "@/lib/utils";
import { useUser } from "modules/user/useUser/useUser";
import { StackedCarousel } from "organisms/stacked-carousel/StackedCarousel";

import { replacePersonaPlaceholders } from "../adonis.helpers";
import { useAdonisChat } from "../AdonisChatProvider/AdonisChatProvider";
import { AdvisorAskInput } from "../AdvisorAskInput/AdvisorAskInput";
import { AdvisorAvatar } from "../AdvisorAvatar/AdvisorAvatar";

import {
  RecommendationSlide,
  RecommendationSlideData,
} from "./RecommendationSlide";

interface AdvisorRecommandationCarouselText {
  header: string;
  link: string;
  chat_box: string;
  chat_submit: string;
}

export interface AdvisorRecommandationCarouselProps {
  recommendations: RecommendationSlideData[];
  ctaText?: AdvisorRecommandationCarouselText;
  className?: string;
  onAskClick: () => void;
  onViewMoreClick: () => void;
}

export const AdvisorRecommandationCarousel: FC<
  AdvisorRecommandationCarouselProps
> = ({ recommendations, ctaText, className, onAskClick, onViewMoreClick }) => {
  const { persona } = useAdonisChat();
  const { user } = useUser();

  const username = user?.firstName ?? "";

  const defaultText: AdvisorRecommandationCarouselText = {
    header: "Recommended<br> for {username}...",
    link: "View your personalized recommendations",
    chat_box: "Looking for something else?",
    chat_submit: "Ask {Name}",
  };

  const mergedText: AdvisorRecommandationCarouselText = {
    header: ctaText?.header ?? defaultText.header,
    link: ctaText?.link ?? defaultText.link,
    chat_box: ctaText?.chat_box ?? defaultText.chat_box,
    chat_submit: ctaText?.chat_submit ?? defaultText.chat_submit,
  };

  const text: AdvisorRecommandationCarouselText = {
    header: replacePersonaPlaceholders(
      mergedText.header.replace("{username}", username),
      persona
    ),
    link: replacePersonaPlaceholders(mergedText.link, persona),
    chat_box: replacePersonaPlaceholders(mergedText.chat_box, persona),
    chat_submit: replacePersonaPlaceholders(mergedText.chat_submit, persona),
  };

  return (
    <div
      className={cn("w-full overflow-hidden", className)}
      style={{
        background:
          "radial-gradient(ellipse at 10% 25%, rgba(255, 255, 255, 0.9) 0%, rgba(238, 218, 197, 0.8) 70%, rgba(222, 193, 156, 0.6) 100%)",
      }}
    >
      <div className="flex flex-col gap-4 px-6 py-10">
        <div className="flex items-center justify-center gap-6">
          <AdvisorAvatar advisor={persona.name} size="large" />

          <span
            className="mt-2 text-left font-serif text-[27px] italic leading-tight"
            dangerouslySetInnerHTML={{ __html: text.header }}
          />
        </div>

        <div className="mb-4 mt-6">
          <StackedCarousel
            data={recommendations}
            slideComponent={RecommendationSlide}
          />
        </div>

        <LinkWithIcon text={text.link} onClick={onViewMoreClick} />

        <AdvisorAskInput
          placeholder={text.chat_box}
          buttonText={text.chat_submit}
          onClick={onAskClick}
        />
      </div>
    </div>
  );
};
